import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { adminAPI, getFileUrl } from "@/services/api";
import { BONUS_TYPES, formatCurrency, formatDate } from "@/utils/constants";
import { getErrorMessage } from "@/utils/errorHelpers";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import { CheckCircle, FileSearch, Loader2, RefreshCw, Wallet, XCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "", label: "Tous" },
  { value: "pending", label: "En attente" },
  { value: "processing", label: "En traitement" },
  { value: "paid", label: "Payes" },
  { value: "rejected", label: "Rejetes" },
  { value: "cancelled", label: "Annules" },
];

const STATUS_BADGES = {
  pending: "bg-amber-100 text-amber-800",
  processing: "bg-blue-100 text-blue-800",
  paid: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-slate-100 text-slate-700",
};

export default function WithdrawalManagementPage() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [payForms, setPayForms] = useState({});
  const [rejectReasons, setRejectReasons] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.listWithdrawals(statusFilter ? { status: statusFilter } : undefined);
      setWithdrawals(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Chargement des retraits impossible"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    return withdrawals.reduce(
      (acc, withdrawal) => {
        acc.count += 1;
        acc.amount += Number(withdrawal.reserved_amount_mc || 0);
        if (withdrawal.status === "pending") acc.pending += 1;
        if (withdrawal.status === "processing") acc.processing += 1;
        return acc;
      },
      { count: 0, amount: 0, pending: 0, processing: 0 }
    );
  }, [withdrawals]);

  const processWithdrawal = async (withdrawalId) => {
    setBusyId(withdrawalId);
    try {
      await adminAPI.processWithdrawal(withdrawalId, "Pris en charge pour verification");
      toast.success("Demande prise en charge");
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, "Action impossible"));
    } finally {
      setBusyId("");
    }
  };

  const rejectWithdrawal = async (withdrawalId) => {
    const reason = (rejectReasons[withdrawalId] || "").trim();
    if (reason.length < 2) {
      toast.error("Indiquez le motif du rejet");
      return;
    }
    setBusyId(withdrawalId);
    try {
      await adminAPI.rejectWithdrawal(withdrawalId, reason);
      toast.success("Demande rejetee");
      setRejectReasons((prev) => ({ ...prev, [withdrawalId]: "" }));
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, "Rejet impossible"));
    } finally {
      setBusyId("");
    }
  };

  const updatePayForm = (withdrawalId, key, value) => {
    setPayForms((prev) => ({
      ...prev,
      [withdrawalId]: { ...(prev[withdrawalId] || {}), [key]: value },
    }));
  };

  const payWithdrawal = async (withdrawalId) => {
    const form = payForms[withdrawalId] || {};
    if (!form.payment_reference || !form.payment_channel) {
      toast.error("Reference et canal de paiement sont obligatoires");
      return;
    }
    const formData = new FormData();
    formData.append("payment_reference", form.payment_reference);
    formData.append("payment_channel", form.payment_channel);
    formData.append("paid_at", form.paid_at || "");
    formData.append("admin_notes", form.admin_notes || "");
    if (form.file) formData.append("file", form.file);

    setBusyId(withdrawalId);
    try {
      await adminAPI.payWithdrawal(withdrawalId, formData);
      toast.success("Retrait confirme et debite du compte partenaire");
      setPayForms((prev) => ({ ...prev, [withdrawalId]: {} }));
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, "Paiement impossible"));
    } finally {
      setBusyId("");
    }
  };

  if (loading) return <LoadingState label="Chargement des paiements bonus..." />;

  return (
    <PageShell data-testid="withdrawal-management-page">
      <PageHeader
        title="Paiements bonus"
        description="Traitement securise des paiements mensuels generes après cloture"
        actions={
          <Button variant="outline" onClick={load} className="gap-2">
            <RefreshCw size={16} /> Actualiser
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-4">
        <AdminStat title="Paiements" value={totals.count} />
        <AdminStat title="Montant affiche" value={formatCurrency(totals.amount)} />
        <AdminStat title="En attente" value={totals.pending} />
        <AdminStat title="En traitement" value={totals.processing} />
      </div>

      <Card className="border-[#E6E4E0] shadow-sm">
        <CardContent className="flex flex-wrap items-center gap-2 p-4">
          {STATUS_OPTIONS.map((option) => (
            <Button
              key={option.value}
              type="button"
              size="sm"
              variant={statusFilter === option.value ? "default" : "outline"}
              className={statusFilter === option.value ? "bg-[#2A4B46] hover:bg-[#1f3a36]" : ""}
              onClick={() => setStatusFilter(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </CardContent>
      </Card>

      <Card className="border-[#E6E4E0] bg-[#F8F5EF] shadow-sm">
        <CardContent className="p-4 text-sm text-[#5C625F]">
          Les lignes de paiement sont generees automatiquement par la cloture mensuelle. L'admin verifie les coordonnees KYC, paie hors plateforme, puis confirme ici avec la reference et la preuve.
        </CardContent>
      </Card>

      <div className="space-y-4">
        {withdrawals.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-[#5C625F]">Aucun paiement pour ce filtre.</CardContent>
          </Card>
        ) : (
          withdrawals.map((withdrawal) => (
            <Card key={withdrawal.id} className="border-[#E6E4E0] shadow-sm">
              <CardHeader className="gap-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <CardTitle className="text-[#2A4B46]">{withdrawal.id}</CardTitle>
                    <p className="mt-1 text-sm text-[#5C625F]">
                      {withdrawal.partner?.full_name || withdrawal.partner_id} - {withdrawal.partner_id}
                    </p>
                    <p className="text-xs text-[#8A8F8C]">{formatDate(withdrawal.created_at)}</p>
                  </div>
                  <div className="text-left md:text-right">
                    <Badge className={STATUS_BADGES[withdrawal.status] || "bg-slate-100 text-slate-700"}>
                      {withdrawal.status_label || withdrawal.status}
                    </Badge>
                    <p className="mt-2 text-2xl font-bold text-[#2A4B46]">{formatCurrency(withdrawal.reserved_amount_mc || 0)}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="grid gap-5 xl:grid-cols-[1fr_1fr]">
                <div className="space-y-4">
                  <InfoBlock title="Coordonnees de paiement">
                    <p>{withdrawal.payment?.method} {withdrawal.payment?.provider || ""}</p>
                    <p>{withdrawal.payment?.account_name}</p>
                    <p>{withdrawal.payment?.account_number}</p>
                    {withdrawal.partner_notes && <p className="mt-2 text-[#5C625F]">{withdrawal.partner_notes}</p>}
                  </InfoBlock>

                  <InfoBlock title="Bonus reserves">
                    <div className="space-y-2">
                      {(withdrawal.bonus_allocations || []).map((allocation) => (
                        <div key={`${allocation.bonus_id}-${allocation.amount_mc}`} className="flex items-center justify-between gap-3 rounded-lg bg-[#F8F5EF] px-3 py-2 text-sm">
                          <span>{BONUS_TYPES[allocation.bonus_type]?.label || allocation.bonus_type}</span>
                          <strong className="text-[#2A4B46]">{formatCurrency(allocation.amount_mc || 0)}</strong>
                        </div>
                      ))}
                    </div>
                  </InfoBlock>
                </div>

                <div className="space-y-4">
                  {withdrawal.status === "pending" && (
                    <ActionBox>
                      <Button disabled={busyId === withdrawal.id} onClick={() => processWithdrawal(withdrawal.id)} className="bg-[#2A4B46] hover:bg-[#1f3a36]">
                        {busyId === withdrawal.id ? <Loader2 className="animate-spin" /> : <Wallet size={16} />}
                        Prendre en charge
                      </Button>
                      <RejectControls
                        withdrawalId={withdrawal.id}
                        value={rejectReasons[withdrawal.id] || ""}
                        onChange={(value) => setRejectReasons((prev) => ({ ...prev, [withdrawal.id]: value }))}
                        onReject={rejectWithdrawal}
                        busy={busyId === withdrawal.id}
                      />
                    </ActionBox>
                  )}

                  {withdrawal.status === "processing" && (
                    <ActionBox>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="Reference paiement" value={payForms[withdrawal.id]?.payment_reference || ""} onChange={(value) => updatePayForm(withdrawal.id, "payment_reference", value)} />
                        <Field label="Canal paiement" value={payForms[withdrawal.id]?.payment_channel || ""} onChange={(value) => updatePayForm(withdrawal.id, "payment_channel", value)} />
                        <Field label="Date paiement" type="datetime-local" value={payForms[withdrawal.id]?.paid_at || ""} onChange={(value) => updatePayForm(withdrawal.id, "paid_at", value)} />
                        <div className="space-y-2">
                          <Label>Preuve paiement</Label>
                          <Input type="file" accept="image/*,.pdf" onChange={(event) => updatePayForm(withdrawal.id, "file", event.target.files?.[0])} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Notes admin</Label>
                        <Textarea value={payForms[withdrawal.id]?.admin_notes || ""} onChange={(event) => updatePayForm(withdrawal.id, "admin_notes", event.target.value)} />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button disabled={busyId === withdrawal.id} onClick={() => payWithdrawal(withdrawal.id)} className="bg-green-700 hover:bg-green-800">
                          {busyId === withdrawal.id ? <Loader2 className="animate-spin" /> : <CheckCircle size={16} />}
                          Confirmer paiement
                        </Button>
                        <RejectControls
                          withdrawalId={withdrawal.id}
                          value={rejectReasons[withdrawal.id] || ""}
                          onChange={(value) => setRejectReasons((prev) => ({ ...prev, [withdrawal.id]: value }))}
                          onReject={rejectWithdrawal}
                          busy={busyId === withdrawal.id}
                        />
                      </div>
                    </ActionBox>
                  )}

                  {withdrawal.payment_execution?.proof_path && (
                    <Button type="button" variant="outline" onClick={() => window.open(getFileUrl(withdrawal.payment_execution.proof_path), "_blank")}>
                      <FileSearch size={16} /> Voir la preuve
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </PageShell>
  );
}

function AdminStat({ title, value }) {
  return (
    <Card className="border-[#E6E4E0] shadow-sm">
      <CardContent className="p-5">
        <p className="text-sm text-[#5C625F]">{title}</p>
        <p className="mt-2 text-2xl font-bold text-[#2A4B46]">{value}</p>
      </CardContent>
    </Card>
  );
}

function InfoBlock({ title, children }) {
  return (
    <div className="rounded-xl bg-[#FDFBF7] p-4">
      <p className="mb-2 text-xs font-semibold uppercase text-[#8A8F8C]">{title}</p>
      <div className="text-sm text-[#2A4B46]">{children}</div>
    </div>
  );
}

function ActionBox({ children }) {
  return <div className="space-y-3 rounded-xl border border-[#E6E4E0] bg-white p-4">{children}</div>;
}

function RejectControls({ withdrawalId, value, onChange, onReject, busy }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <Input value={value} onChange={(event) => onChange(event.target.value)} placeholder="Motif du rejet" />
      <Button type="button" variant="outline" disabled={busy} onClick={() => onReject(withdrawalId)}>
        <XCircle size={16} /> Rejeter
      </Button>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  const id = label.toLowerCase().replaceAll(" ", "-");
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}
